import { Box, Button, Paper, Typography } from '@mui/material';
import { fetchAdminAttendeesDetails } from 'API/attendeeApi';
import { getUserInfo } from 'API/authentication';
import BackButton from 'Components/BackButton';
import RadioButtonGroup from 'Components/FormControls/RadioButtonGroup';
import { t } from 'i18next';
import { useCallback, useEffect, useMemo } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import './MultipleSession.scss';

const MultipleSession = ({ eventDetails, onPrevious, onNext, onCancel }) => {
  const dispatch = useDispatch();
  const { setValue, control } = useFormContext();
  const selectedOption = useWatch({ name: 'selectedOption', control });

  const { almUserId, authToken } = useParams();
  const userInfo = getUserInfo();

  const { getAdminAttendeeDetailsData } = useSelector((state) => state.attendee);

  useEffect(() => {
    if (almUserId && authToken) {
      const adminPayload = { almUserId, authToken };
      dispatch(fetchAdminAttendeesDetails({ adminpayload: adminPayload })).unwrap();
    }
  }, [dispatch, almUserId, authToken]);

  const userRegistrationType = useMemo(() => {
    return getAdminAttendeeDetailsData?.fields?.contactType || userInfo?.contactType;
  }, [getAdminAttendeeDetailsData?.fields?.contactType, userInfo?.contactType]);

  const matchingSessions = useMemo(() => {
    const sessions = [
      ...(eventDetails?.includedSessions?.flatMap((section) =>
        section?.sessions?.map((session) => ({
          ...session,
          sectionTitle: section.sectionTitle,
          isMandatory: true
        }))
      ) || []),
      ...(eventDetails?.limitedAvailableSessions?.flatMap((section) =>
        section?.sessions?.map((session) => ({
          ...session,
          sectionTitle: section.sectionTitle,
          isMandatory: false
        }))
      ) || [])
    ];

    return sessions?.map((session) => ({
      ...session,
      fees: session?.fees?.filter((fee) => fee?.registrationType === userRegistrationType)
    }));
  }, [eventDetails, userRegistrationType]);

  const filteredSessions = useMemo(() => {
    return matchingSessions?.filter((session) => session?.fees?.length > 0);
  }, [matchingSessions]);

  const uniqueFeeNames = useMemo(() => {
    const filteredFees = filteredSessions?.flatMap((session) =>
      session?.fees?.map((fee) => fee.feeName)
    );
    return [...new Set(filteredFees)];
  }, [filteredSessions]);

  const handleRadioChange = useCallback(
    (event) => {
      const selectedFeeName = event.target.value;
      setValue('selectedOption', selectedFeeName);

      const updatedSessions = matchingSessions
        ?.map((session) => {
          const matchingFee = session?.fees?.find((fee) => fee.feeName === selectedFeeName);
          return matchingFee ? { ...session, fees: [matchingFee] } : null;
        })
        .filter((session) => session !== null);

      setValue('filteredSessionsData', updatedSessions);
    },
    [matchingSessions, setValue]
  );

  return (
    <Paper className="multiple-session-main">
      <BackButton />
      <Typography className="text-title" variant="h6" component="h2">
        {t(eventDetails?.title)}
      </Typography>
      <ul className="custom-list">
        <li>{t('multipleSession.admitionItem')}</li>
      </ul>
      <Typography className="instruction-text" component="p">
        {t('multipleSession.instruction')}
      </Typography>

      <Box>
        <RadioButtonGroup
          name="selectedOption"
          options={uniqueFeeNames?.map((feeName) => ({
            value: feeName,
            label: feeName
          }))}
          onChange={handleRadioChange}
          defaultValue={selectedOption}
        />
      </Box>
      <div className="seprator"></div>
      <Button className="cancel-btn" onClick={onCancel}>
        {t('button.cancel')}
      </Button>
      <Button className="previous-btn" onClick={onPrevious}>
        {t('button.previous')}
      </Button>
      <Button className="submit-btn" onClick={onNext}>
        {t('button.next')}
      </Button>
    </Paper>
  );
};

export default MultipleSession;
